import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@src/lib/utils"
import Spinner from "../../icons/Spinner"

const buttonVariants = cva(
  "sl-inline-flex sl-items-center sl-justify-center sl-whitespace-nowrap sl-rounded-md sl-text-sm sl-font-medium sl-transition-colors focus-visible:sl-outline-none focus-visible:sl-ring-1 focus-visible:sl-ring-ring disabled:sl-pointer-events-none disabled:sl-opacity-50",
  {
    variants: {
      variant: {
        default:
          "sl-bg-primary sl-text-primary-foreground sl-shadow hover:sl-bg-primary/90",
        destructive:
          "sl-bg-destructive sl-text-destructive-foreground sl-shadow-sm hover:sl-bg-destructive/90",
        outline:
          "sl-border sl-border-input sl-bg-transparent sl-shadow-sm hover:sl-bg-accent hover:sl-text-accent-foreground",
        secondary:
          "sl-bg-secondary sl-text-secondary-foreground sl-shadow-sm hover:sl-bg-secondary/80",
        ghost: "hover:sl-bg-accent hover:sl-text-accent-foreground",
        link: "sl-text-primary sl-underline-offset-4 hover:sl-underline"
      },
      size: {
        default: "sl-h-9 sl-px-4 sl-py-2",
        sm: "sl-h-8 sl-rounded-md sl-px-3 sl-text-xs",
        lg: "sl-h-10 sl-rounded-md sl-px-8",
        icon: "sl-h-9 sl-w-9"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        onClick={!loading ? props.onClick : undefined}
      >
        <div className="sl-flex sl-justify-center sl-items-center sl-gap-2">
          {props.children}
          {loading && <Spinner />}
        </div>
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
